import { useCallback, useEffect, useMemo, useState } from "react";
import AppHeader from "@/components/AppHeader";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { toast } from "sonner";
import {
  Loader2,
  Mail,
  Calendar,
  Clock,
  Globe,
  Send,
  TestTube2,
  Save,
  X,
  Plus,
  Power,
  ShieldCheck,
  Zap,
  DollarSign,
} from "lucide-react";

const DAYS = [
  { value: 0, short: "Mon", long: "Monday" },
  { value: 1, short: "Tue", long: "Tuesday" },
  { value: 2, short: "Wed", long: "Wednesday" },
  { value: 3, short: "Thu", long: "Thursday" },
  { value: 4, short: "Fri", long: "Friday" },
  { value: 5, short: "Sat", long: "Saturday" },
  { value: 6, short: "Sun", long: "Sunday" },
];

const COMMON_TZ = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
  "Europe/London",
  "Europe/Paris",
  "Africa/Lagos",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "UTC",
];

function formatNextRun(iso) {
  if (!iso) return "Not scheduled";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: "long",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    });
  } catch {
    return iso;
  }
}

function formatLastSent(iso) {
  if (!iso) return "Never";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [newRecipient, setNewRecipient] = useState("");
  const [dailyTestEmail, setDailyTestEmail] = useState("");
  const [weeklyTestEmail, setWeeklyTestEmail] = useState("");
  const [testingDaily, setTestingDaily] = useState(false);
  const [testingWeekly, setTestingWeekly] = useState(false);
  const [runningDaily, setRunningDaily] = useState(false);
  const [runningWeekly, setRunningWeekly] = useState(false);
  const [approvalMode, setApprovalMode] = useState(null);
  const [savingApproval, setSavingApproval] = useState(false);
  const [commissionsSettings, setCommissionsSettings] = useState(null);
  const [bookingFeeInput, setBookingFeeInput] = useState("");
  const [splitInput, setSplitInput] = useState("");
  const [savingCommissions, setSavingCommissions] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-settings");
      setSettings(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/settings/approval")
      .then(({ data }) => {
        if (!cancelled) setApprovalMode(data?.mode || "auto");
      })
      .catch(() => {
        if (!cancelled) setApprovalMode("auto");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/settings/commissions")
      .then(({ data }) => {
        if (cancelled) return;
        setCommissionsSettings(data);
        setBookingFeeInput(data?.booking_fee != null ? String(data.booking_fee) : "");
        setSplitInput(
          data?.commission_split != null
            ? String(Math.round(data.commission_split * 1000) / 10)
            : ""
        );
      })
      .catch(() => {
        if (!cancelled) setCommissionsSettings({ booking_fee: 9.5, commission_split: 0.5 });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const saveCommissions = async () => {
    const fee = parseFloat(bookingFeeInput);
    const splitPct = parseFloat(splitInput);
    if (!Number.isFinite(fee) || fee < 0) {
      toast.error("Booking fee must be a non-negative number.");
      return;
    }
    if (!Number.isFinite(splitPct) || splitPct < 0 || splitPct > 100) {
      toast.error("Commission split must be a percentage between 0 and 100.");
      return;
    }
    setSavingCommissions(true);
    try {
      const { data } = await api.put("/admin/settings/commissions", {
        booking_fee: Math.round(fee * 100) / 100,
        commission_split: Math.round(splitPct * 10) / 1000, // % → 0..1, 1 decimal of %
      });
      setCommissionsSettings(data);
      toast.success("Commission rates saved", {
        description: "New rates apply to commissions logged from now on.",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save commission rates.");
    } finally {
      setSavingCommissions(false);
    }
  };

  const updateApprovalMode = async (next) => {
    if (!next || next === approvalMode || savingApproval) return;
    setSavingApproval(true);
    try {
      const { data } = await api.put("/admin/settings/approval", { mode: next });
      setApprovalMode(data?.mode || next);
      toast.success(
        next === "auto"
          ? "Auto-approve mode on — new entries skip the review step."
          : "Manual review on — new entries land as drafts and must be submitted/approved."
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update approval mode.");
    } finally {
      setSavingApproval(false);
    }
  };

  const update = (patch) =>
    setSettings((s) => (s ? { ...s, ...patch } : s));

  const addRecipient = () => {
    const e = newRecipient.trim().toLowerCase();
    if (!e) return;
    if (!e.includes("@") || !e.includes(".")) {
      toast.error("Please enter a valid email.");
      return;
    }
    if (settings.recipients.includes(e)) {
      toast.error("That email is already in the list.");
      return;
    }
    update({ recipients: [...settings.recipients, e] });
    setNewRecipient("");
  };

  const removeRecipient = (e) =>
    update({ recipients: settings.recipients.filter((x) => x !== e) });

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/email-settings", {
        enabled: settings.enabled,
        day_of_week: settings.day_of_week,
        hour: settings.hour,
        minute: settings.minute,
        timezone: settings.timezone,
        recipients: settings.recipients,
        daily_reminder: settings.daily_reminder,
        weekly_reminder: settings.weekly_reminder,
      });
      setSettings(data);
      toast.success("Settings saved", {
        description: data.enabled
          ? `Next summary: ${formatNextRun(data.next_run_at)}`
          : "Weekly emails are disabled.",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save settings.");
    } finally {
      setSaving(false);
    }
  };

  const updateDaily = (patch) =>
    setSettings((s) => ({ ...s, daily_reminder: { ...s.daily_reminder, ...patch } }));
  const updateWeekly = (patch) =>
    setSettings((s) => ({ ...s, weekly_reminder: { ...s.weekly_reminder, ...patch } }));

  const handleDailyTest = async () => {
    const to = dailyTestEmail.trim();
    if (!to) return toast.error("Enter an email for the daily test.");
    setTestingDaily(true);
    try {
      await api.post("/admin/reminders/daily/test", { to });
      toast.success("Daily reminder test sent", { description: `Check ${to}.` });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test failed.");
    } finally {
      setTestingDaily(false);
    }
  };

  const handleWeeklyTest = async () => {
    const to = weeklyTestEmail.trim();
    if (!to) return toast.error("Enter an email for the weekly test.");
    setTestingWeekly(true);
    try {
      await api.post("/admin/reminders/weekly/test", { to });
      toast.success("Weekly reminder test sent", { description: `Check ${to}.` });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test failed.");
    } finally {
      setTestingWeekly(false);
    }
  };

  const handleRunDaily = async () => {
    setRunningDaily(true);
    try {
      const { data } = await api.post("/admin/reminders/daily/run-now");
      toast.success(
        data.count === 0
          ? "No reminders needed"
          : `Daily reminders sent to ${data.count} freelancer${data.count > 1 ? "s" : ""}`,
        { description: data.count > 0 ? data.sent_to.join(", ") : "Everyone has logged hours today." }
      );
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Run failed.");
    } finally {
      setRunningDaily(false);
    }
  };

  const handleRunWeekly = async () => {
    setRunningWeekly(true);
    try {
      const { data } = await api.post("/admin/reminders/weekly/run-now");
      toast.success(
        data.count === 0
          ? "No reminders needed"
          : `Weekly reminders sent to ${data.count} freelancer${data.count > 1 ? "s" : ""}`,
        { description: data.count > 0 ? data.sent_to.join(", ") : "Everyone has logged hours this week." }
      );
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Run failed.");
    } finally {
      setRunningWeekly(false);
    }
  };

  const handleTest = async () => {
    const to = testEmail.trim();
    if (!to) {
      toast.error("Enter an email to send the test to.");
      return;
    }
    setTesting(true);
    try {
      await api.post("/admin/email-settings/test", { to });
      toast.success("Test email sent", {
        description: `Check ${to} in a few seconds.`,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test failed.");
    } finally {
      setTesting(false);
    }
  };

  const handleSendNow = async () => {
    if (!settings?.recipients?.length) {
      toast.error("Add at least one recipient first.");
      return;
    }
    setSendingNow(true);
    try {
      const { data } = await api.post("/admin/email-settings/send-now");
      toast.success("Weekly summary sent", {
        description: `Delivered to ${data.sent_to.length} recipient${data.sent_to.length > 1 ? "s" : ""}.`,
      });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send.");
    } finally {
      setSendingNow(false);
    }
  };

  const hoursOptions = useMemo(
    () =>
      Array.from({ length: 24 }, (_, h) => ({
        value: h,
        label: `${((h % 12) || 12)}:00 ${h < 12 ? "AM" : "PM"}`,
      })),
    []
  );

  if (loading || !settings) {
    return (
      <div className="min-h-screen bg-slate-50">
        <AppHeader />
        <div className="flex items-center justify-center h-96">
          <Loader2 className="animate-spin text-slate-400" size={20} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <main
        className="max-w-3xl mx-auto px-4 sm:px-6 py-8"
        data-testid="admin-settings-page"
      >
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-slate-900">Settings</h2>
          <p className="text-sm text-slate-500 mt-1">
            Approval workflow and automated email schedule.
          </p>
        </div>

        {/* Approval mode */}
        <div
          className="bg-white rounded-2xl border border-slate-200 p-5 mb-5"
          data-testid="approval-mode-section"
        >
          <div className="flex items-start gap-3 mb-4">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
              style={{
                background: `linear-gradient(135deg, ${BRAND.teal}20, ${BRAND.purple}20)`,
              }}
            >
              <ShieldCheck style={{ color: BRAND.teal }} size={18} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900">
                Approval mode
              </h3>
              <p className="text-sm text-slate-500 mt-0.5">
                Pick how freelancer time entries become billable.
              </p>
            </div>
          </div>

          {approvalMode === null ? (
            <div className="py-6 flex items-center justify-center text-slate-400">
              <Loader2 className="animate-spin" size={18} />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ModeOption
                active={approvalMode === "auto"}
                disabled={savingApproval}
                onClick={() => updateApprovalMode("auto")}
                Icon={Zap}
                title="Auto-approve"
                description="New entries are approved on creation. Freelancers can still edit them. Best for small, trusted teams."
                badge="Default"
                testId="approval-mode-auto"
              />
              <ModeOption
                active={approvalMode === "manual"}
                disabled={savingApproval}
                onClick={() => updateApprovalMode("manual")}
                Icon={ShieldCheck}
                title="Manual review"
                description="Freelancers submit each entry; you approve before it counts. Use when you want a sign-off step."
                testId="approval-mode-manual"
              />
            </div>
          )}
        </div>

        {/* Commissions */}
        <div
          className="bg-white rounded-2xl border border-slate-200 p-5 mb-5"
          data-testid="commissions-settings-section"
        >
          <div className="flex items-start gap-3 mb-4">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
              style={{
                background: `linear-gradient(135deg, ${BRAND.teal}20, ${BRAND.purple}20)`,
              }}
            >
              <DollarSign style={{ color: BRAND.teal }} size={18} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900">
                Commissions
              </h3>
              <p className="text-sm text-slate-500 mt-0.5">
                Booking fee and freelancer split. Existing commissions keep their
                original rates — changes here only apply to new submissions.
                Enable per-freelancer on the <strong>Team</strong> page.
              </p>
            </div>
          </div>

          {commissionsSettings === null ? (
            <div className="py-6 flex items-center justify-center text-slate-400">
              <Loader2 className="animate-spin" size={18} />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">
                    Booking fee ($ per booking)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                      $
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={bookingFeeInput}
                      onChange={(e) => setBookingFeeInput(e.target.value)}
                      data-testid="commissions-booking-fee"
                      className="w-full pl-7 pr-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:outline-none focus:ring-2 bg-white"
                      style={{ "--tw-ring-color": BRAND.teal }}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">
                    Freelancer share (% of booking fee)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={splitInput}
                      onChange={(e) => setSplitInput(e.target.value)}
                      data-testid="commissions-split"
                      className="w-full pr-8 px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:outline-none focus:ring-2 bg-white"
                      style={{ "--tw-ring-color": BRAND.teal }}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                      %
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={saveCommissions}
                  disabled={savingCommissions}
                  data-testid="commissions-save"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold transition disabled:opacity-70"
                  style={{
                    background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
                  }}
                >
                  {savingCommissions ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Save size={14} />
                  )}
                  Save commission rates
                </button>
              </div>
            </>
          )}
        </div>

        {/* Enabled toggle */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                style={{
                  background: `linear-gradient(135deg, ${BRAND.teal}20, ${BRAND.purple}20)`,
                }}
              >
                <Mail style={{ color: BRAND.teal }} size={18} />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  Weekly email summary
                </h3>
                <p className="text-sm text-slate-500 mt-0.5">
                  Sends total hours + per-freelancer breakdown for the week
                  ending on the send day.
                </p>
                <div className="mt-3 text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
                  <span>
                    <span className="font-semibold text-slate-700">Next:</span>{" "}
                    <span data-testid="next-run">
                      {settings.enabled
                        ? formatNextRun(settings.next_run_at)
                        : "Disabled"}
                    </span>
                  </span>
                  <span>
                    <span className="font-semibold text-slate-700">Last sent:</span>{" "}
                    <span data-testid="last-sent">
                      {formatLastSent(settings.last_sent_at)}
                    </span>
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={() => update({ enabled: !settings.enabled })}
              data-testid="toggle-enabled"
              className={`relative inline-flex items-center h-6 w-11 rounded-full transition shrink-0 ${
                settings.enabled ? "" : "bg-slate-300"
              }`}
              style={settings.enabled ? { background: BRAND.teal } : undefined}
              aria-pressed={settings.enabled}
            >
              <span
                className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition ${
                  settings.enabled ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>
        </div>

        {/* Schedule */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-5">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Calendar size={14} style={{ color: BRAND.teal }} />
            Schedule
          </h3>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Day of week
            </label>
            <div
              className="grid grid-cols-7 gap-1.5"
              data-testid="day-picker"
            >
              {DAYS.map((d) => (
                <button
                  key={d.value}
                  onClick={() => update({ day_of_week: d.value })}
                  data-testid={`day-${d.value}`}
                  disabled={!settings.enabled}
                  className={`py-2 text-xs font-semibold rounded-md border transition disabled:opacity-50 disabled:cursor-not-allowed ${
                    settings.day_of_week === d.value
                      ? "text-white border-transparent"
                      : "text-slate-600 border-slate-200 hover:border-slate-300"
                  }`}
                  style={
                    settings.day_of_week === d.value
                      ? {
                          background: `linear-gradient(135deg, ${BRAND.teal}, ${BRAND.purple})`,
                        }
                      : undefined
                  }
                >
                  {d.short}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                <Clock size={12} className="inline mr-1" />
                Hour
              </label>
              <select
                value={settings.hour}
                onChange={(e) => update({ hour: parseInt(e.target.value) })}
                data-testid="hour-select"
                disabled={!settings.enabled}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:outline-none focus:ring-2 bg-white disabled:opacity-50"
                style={{ "--tw-ring-color": BRAND.teal }}
              >
                {hoursOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                Minute
              </label>
              <select
                value={settings.minute}
                onChange={(e) => update({ minute: parseInt(e.target.value) })}
                data-testid="minute-select"
                disabled={!settings.enabled}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:outline-none focus:ring-2 bg-white disabled:opacity-50"
                style={{ "--tw-ring-color": BRAND.teal }}
              >
                {[0, 15, 30, 45].map((m) => (
                  <option key={m} value={m}>
                    :{String(m).padStart(2, "0")}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                <Globe size={12} className="inline mr-1" />
                Timezone
              </label>
              <select
                value={settings.timezone}
                onChange={(e) => update({ timezone: e.target.value })}
                data-testid="timezone-select"
                disabled={!settings.enabled}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:outline-none focus:ring-2 bg-white disabled:opacity-50"
                style={{ "--tw-ring-color": BRAND.teal }}
              >
                {COMMON_TZ.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
                {!COMMON_TZ.includes(settings.timezone) && (
                  <option value={settings.timezone}>{settings.timezone}</option>
                )}
              </select>
            </div>
          </div>

          <p className="mt-3 text-xs text-slate-500">
            Email sends every{" "}
            <span className="font-semibold text-slate-700">
              {DAYS[settings.day_of_week].long}
            </span>{" "}
            at{" "}
            <span className="font-semibold text-slate-700">
              {((settings.hour % 12) || 12)}:{String(settings.minute).padStart(2, "0")}{" "}
              {settings.hour < 12 ? "AM" : "PM"}
            </span>{" "}
            ({settings.timezone}).
          </p>
        </div>

        {/* Recipients */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-5">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Mail size={14} style={{ color: BRAND.teal }} />
            Recipients
          </h3>

          <div className="flex flex-wrap gap-2 mb-3" data-testid="recipients-list">
            {settings.recipients.length === 0 ? (
              <p className="text-sm text-slate-500 italic">
                No recipients yet. Add at least one to enable sending.
              </p>
            ) : (
              settings.recipients.map((e) => (
                <span
                  key={e}
                  data-testid={`recipient-${e}`}
                  className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full text-sm text-slate-700"
                  style={{ background: `${BRAND.teal}12`, color: BRAND.teal }}
                >
                  {e}
                  <button
                    onClick={() => removeRecipient(e)}
                    className="hover:bg-white/50 rounded-full p-0.5"
                    title="Remove"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))
            )}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              addRecipient();
            }}
            className="flex gap-2"
          >
            <input
              type="email"
              value={newRecipient}
              onChange={(e) => setNewRecipient(e.target.value)}
              placeholder="Add an email…"
              data-testid="new-recipient-input"
              className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 bg-white"
              style={{ "--tw-ring-color": BRAND.teal }}
            />
            <button
              type="submit"
              data-testid="add-recipient-button"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              <Plus size={14} />
              Add
            </button>
          </form>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 justify-between mb-5">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleSendNow}
              disabled={sendingNow || !settings.recipients.length}
              data-testid="send-now-button"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-700 border border-slate-200 hover:bg-slate-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sendingNow ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Send now
            </button>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-button"
            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-white font-semibold transition disabled:opacity-70"
            style={{
              background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
            }}
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save changes
          </button>
        </div>

        {/* Test email */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-2 flex items-center gap-2">
            <TestTube2 size={14} style={{ color: BRAND.purple }} />
            Send a test email
          </h3>
          <p className="text-xs text-slate-500 mb-3">
            Sends a sample summary to a single address so you can preview it.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="Your email to test…"
              data-testid="test-email-input"
              className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 bg-white"
              style={{ "--tw-ring-color": BRAND.teal }}
            />
            <button
              onClick={handleTest}
              disabled={testing || !testEmail.trim()}
              data-testid="test-send-button"
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition disabled:opacity-70"
              style={{
                background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
              }}
            >
              {testing ? <Loader2 size={14} className="animate-spin" /> : <TestTube2 size={14} />}
              Send test
            </button>
          </div>
        </div>

        {/* ============ FREELANCER REMINDERS ============ */}
        <div className="mt-10 mb-4">
          <h2 className="text-xl font-bold text-slate-900">Freelancer reminders</h2>
          <p className="text-sm text-slate-500 mt-1">
            Automated nudges to freelancers who haven't logged any hours in the window.
            Uses the same timezone as the weekly summary.
          </p>
        </div>

        {/* Daily reminder */}
        <div
          className="bg-white rounded-2xl border border-slate-200 p-5 mb-5"
          data-testid="daily-reminder-card"
        >
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-start gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                style={{ background: `${BRAND.teal}18` }}
              >
                <Clock style={{ color: BRAND.teal }} size={18} />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900">Daily reminder</h3>
                <p className="text-sm text-slate-500 mt-0.5">
                  Emails freelancers at end-of-day if they logged <strong>zero hours today</strong>.
                </p>
                <div className="mt-2 text-xs text-slate-500">
                  <span className="font-semibold text-slate-700">Next:</span>{" "}
                  <span data-testid="daily-next-run">
                    {settings.daily_reminder?.enabled
                      ? formatNextRun(settings.daily_reminder_next_run_at)
                      : "Disabled"}
                  </span>
                  {settings.daily_reminder?.last_sent_at && (
                    <>
                      {" · "}
                      <span className="font-semibold text-slate-700">Last:</span>{" "}
                      {formatLastSent(settings.daily_reminder.last_sent_at)}
                    </>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={() =>
                updateDaily({ enabled: !settings.daily_reminder?.enabled })
              }
              data-testid="toggle-daily-enabled"
              className={`relative inline-flex items-center h-6 w-11 rounded-full transition shrink-0 ${
                settings.daily_reminder?.enabled ? "" : "bg-slate-300"
              }`}
              style={
                settings.daily_reminder?.enabled ? { background: BRAND.teal } : undefined
              }
            >
              <span
                className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition ${
                  settings.daily_reminder?.enabled ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Hour</label>
              <select
                value={settings.daily_reminder?.hour ?? 18}
                onChange={(e) => updateDaily({ hour: parseInt(e.target.value) })}
                data-testid="daily-hour-select"
                disabled={!settings.daily_reminder?.enabled}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:outline-none focus:ring-2 bg-white disabled:opacity-50"
                style={{ "--tw-ring-color": BRAND.teal }}
              >
                {hoursOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Minute</label>
              <select
                value={settings.daily_reminder?.minute ?? 0}
                onChange={(e) => updateDaily({ minute: parseInt(e.target.value) })}
                data-testid="daily-minute-select"
                disabled={!settings.daily_reminder?.enabled}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:outline-none focus:ring-2 bg-white disabled:opacity-50"
                style={{ "--tw-ring-color": BRAND.teal }}
              >
                {[0, 15, 30, 45].map((m) => (
                  <option key={m} value={m}>
                    :{String(m).padStart(2, "0")}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Days</label>
              <div className="flex items-center gap-2 h-[38px]">
                <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!settings.daily_reminder?.weekdays_only}
                    onChange={(e) => updateDaily({ weekdays_only: e.target.checked })}
                    data-testid="daily-weekdays-only"
                    disabled={!settings.daily_reminder?.enabled}
                    className="h-4 w-4 rounded"
                    style={{ accentColor: BRAND.teal }}
                  />
                  Weekdays only
                </label>
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              value={dailyTestEmail}
              onChange={(e) => setDailyTestEmail(e.target.value)}
              placeholder="Send test to…"
              data-testid="daily-test-email-input"
              className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 bg-white text-sm"
              style={{ "--tw-ring-color": BRAND.teal }}
            />
            <button
              onClick={handleDailyTest}
              disabled={testingDaily || !dailyTestEmail.trim()}
              data-testid="daily-test-button"
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 border border-slate-200 hover:bg-slate-50 transition disabled:opacity-50"
            >
              {testingDaily ? <Loader2 size={14} className="animate-spin" /> : <TestTube2 size={14} />}
              Send test
            </button>
            <button
              onClick={handleRunDaily}
              disabled={runningDaily}
              data-testid="daily-run-now-button"
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white transition disabled:opacity-70"
              style={{
                background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
              }}
              title="Send daily reminders right now to everyone missing today"
            >
              {runningDaily ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Run now
            </button>
          </div>
        </div>

        {/* Weekly reminder */}
        <div
          className="bg-white rounded-2xl border border-slate-200 p-5 mb-5"
          data-testid="weekly-reminder-card"
        >
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-start gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                style={{ background: `${BRAND.purple}18` }}
              >
                <Calendar style={{ color: BRAND.purple }} size={18} />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900">Weekly reminder</h3>
                <p className="text-sm text-slate-500 mt-0.5">
                  Emails freelancers who logged <strong>zero hours this week</strong> (Mon–Sun).
                </p>
                <div className="mt-2 text-xs text-slate-500">
                  <span className="font-semibold text-slate-700">Next:</span>{" "}
                  <span data-testid="weekly-next-run">
                    {settings.weekly_reminder?.enabled
                      ? formatNextRun(settings.weekly_reminder_next_run_at)
                      : "Disabled"}
                  </span>
                  {settings.weekly_reminder?.last_sent_at && (
                    <>
                      {" · "}
                      <span className="font-semibold text-slate-700">Last:</span>{" "}
                      {formatLastSent(settings.weekly_reminder.last_sent_at)}
                    </>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={() =>
                updateWeekly({ enabled: !settings.weekly_reminder?.enabled })
              }
              data-testid="toggle-weekly-reminder-enabled"
              className={`relative inline-flex items-center h-6 w-11 rounded-full transition shrink-0 ${
                settings.weekly_reminder?.enabled ? "" : "bg-slate-300"
              }`}
              style={
                settings.weekly_reminder?.enabled ? { background: BRAND.purple } : undefined
              }
            >
              <span
                className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition ${
                  settings.weekly_reminder?.enabled ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Day of week</label>
            <div className="grid grid-cols-7 gap-1.5" data-testid="weekly-reminder-day-picker">
              {DAYS.map((d) => (
                <button
                  key={d.value}
                  onClick={() => updateWeekly({ day_of_week: d.value })}
                  data-testid={`weekly-reminder-day-${d.value}`}
                  disabled={!settings.weekly_reminder?.enabled}
                  className={`py-2 text-xs font-semibold rounded-md border transition disabled:opacity-50 ${
                    settings.weekly_reminder?.day_of_week === d.value
                      ? "text-white border-transparent"
                      : "text-slate-600 border-slate-200 hover:border-slate-300"
                  }`}
                  style={
                    settings.weekly_reminder?.day_of_week === d.value
                      ? {
                          background: `linear-gradient(135deg, ${BRAND.teal}, ${BRAND.purple})`,
                        }
                      : undefined
                  }
                >
                  {d.short}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Hour</label>
              <select
                value={settings.weekly_reminder?.hour ?? 17}
                onChange={(e) => updateWeekly({ hour: parseInt(e.target.value) })}
                data-testid="weekly-reminder-hour-select"
                disabled={!settings.weekly_reminder?.enabled}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:outline-none focus:ring-2 bg-white disabled:opacity-50"
                style={{ "--tw-ring-color": BRAND.teal }}
              >
                {hoursOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Minute</label>
              <select
                value={settings.weekly_reminder?.minute ?? 0}
                onChange={(e) => updateWeekly({ minute: parseInt(e.target.value) })}
                data-testid="weekly-reminder-minute-select"
                disabled={!settings.weekly_reminder?.enabled}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:outline-none focus:ring-2 bg-white disabled:opacity-50"
                style={{ "--tw-ring-color": BRAND.teal }}
              >
                {[0, 15, 30, 45].map((m) => (
                  <option key={m} value={m}>
                    :{String(m).padStart(2, "0")}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              value={weeklyTestEmail}
              onChange={(e) => setWeeklyTestEmail(e.target.value)}
              placeholder="Send test to…"
              data-testid="weekly-reminder-test-email-input"
              className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 bg-white text-sm"
              style={{ "--tw-ring-color": BRAND.teal }}
            />
            <button
              onClick={handleWeeklyTest}
              disabled={testingWeekly || !weeklyTestEmail.trim()}
              data-testid="weekly-reminder-test-button"
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 border border-slate-200 hover:bg-slate-50 transition disabled:opacity-50"
            >
              {testingWeekly ? <Loader2 size={14} className="animate-spin" /> : <TestTube2 size={14} />}
              Send test
            </button>
            <button
              onClick={handleRunWeekly}
              disabled={runningWeekly}
              data-testid="weekly-reminder-run-now-button"
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white transition disabled:opacity-70"
              style={{
                background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
              }}
              title="Send weekly reminders right now to everyone missing this week"
            >
              {runningWeekly ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Run now
            </button>
          </div>
        </div>

        <p className="text-xs text-slate-500 mt-3">
          💡 Changes to reminder settings are saved when you click <strong>Save changes</strong> at the top.
        </p>
      </main>
    </div>
  );
}

function ModeOption({
  active,
  disabled,
  onClick,
  Icon,
  title,
  description,
  badge,
  testId,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`text-left rounded-xl border p-4 transition disabled:opacity-70 ${
        active
          ? "border-transparent shadow-sm"
          : "border-slate-200 hover:border-slate-300"
      }`}
      style={
        active
          ? {
              background: `${BRAND.teal}12`,
              borderColor: `${BRAND.teal}66`,
            }
          : undefined
      }
    >
      <div className="flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
          style={{
            background: active ? BRAND.teal : "#e2e8f0",
            color: active ? "#fff" : "#475569",
          }}
        >
          <Icon size={18} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-900">{title}</span>
            {badge && (
              <span
                className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                style={{ background: `${BRAND.purple}15`, color: BRAND.purple }}
              >
                {badge}
              </span>
            )}
            {active && (
              <span
                className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                style={{ background: BRAND.teal, color: "#fff" }}
              >
                Active
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-1">{description}</p>
        </div>
      </div>
    </button>
  );
}
